import { hashKey, useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Batch, IBatchesCriteria, SortDirection, TemplateType } from '@e-origin/shared';

import { queryClient } from '@hooks/queryClient';
import { showErrorToast } from '@hooks/showErrorToast';
import { request } from '@utils';

export const defaultAdminBatchesCriteria: IBatchesCriteria = {
  filters: { filterByUsers: [], filterByNotifications: [], searchQuery: '', templateType: TemplateType.HIGH_VALUE_H1, isExport: false },
  pagination: { page: 1, size: 10, direction: 1, searchToken: '', currentToken: '' },
  sorting: { field: 'counter', direction: SortDirection.DESC },
};

const parseUrlToCriteria = (searchParams: URLSearchParams): IBatchesCriteria => {
  const filters: IBatchesCriteria['filters'] = {
    filterByUsers: [],
    filterByNotifications: [],
    templateType: TemplateType.HIGH_VALUE_H1,
    isExport: false,
  };

  filters.searchQuery = searchParams.get('searchQuery') || '';
  filters.templateType = (searchParams.get('templateType') as TemplateType) || TemplateType.HIGH_VALUE_H1;
  filters.isExport = (searchParams.get('isExport') || 'false') === 'true';

  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '10', 10);

  const sortField = searchParams.get('sortField') || 'counter';
  const sortDir = (searchParams.get('sortDir') || SortDirection.DESC) as SortDirection;

  return {
    filters,
    pagination: { page, size },
    sorting: { field: sortField, direction: sortDir },
  };
};

const criteriaToSearchParams = (criteria: IBatchesCriteria): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (criteria.filters.searchQuery) {
    searchParams.set('searchQuery', criteria.filters.searchQuery);
  }

  searchParams.set('templateType', criteria.filters.templateType);
  searchParams.set('isExport', criteria.filters.isExport.toString());

  searchParams.set('page', criteria.pagination.page.toString());
  searchParams.set('size', criteria.pagination.size.toString());

  searchParams.set('sortField', criteria.sorting.field);
  searchParams.set('sortDir', criteria.sorting.direction);

  return searchParams;
};

export const getInitialBatchCriteria = (searchParams: URLSearchParams): IBatchesCriteria => {
  if (searchParams.toString()) {
    return parseUrlToCriteria(searchParams);
  }
  return defaultAdminBatchesCriteria;
};

export const useBatchCriteriaUrlSync = (criteria?: IBatchesCriteria) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!criteria) return;

    const currentCriteria = parseUrlToCriteria(searchParams);
    if (isEqual(currentCriteria, criteria)) return;

    const newParams = criteriaToSearchParams(criteria);
    setSearchParams(newParams, { replace: true });
  }, [criteria]);
};

const fetchAdminBatches = async (criteria: IBatchesCriteria): Promise<{ list: Batch[]; total: number }> => {
  try {
    const {
      data: { list, totalItems },
    } = await request({
      path: `admin/batches`,
      method: 'POST',
      authenticate: true,
      dataObject: { criteria },
    });
    return { list: list.map((b) => ({ ...b, createdAt: new Date(b.createdAt), updatedAt: new Date(b.updatedAt) })), total: totalItems };
  } catch (error) {
    showErrorToast(error, 'Failed to fetch admin batches');
    return { list: [], total: 0 };
  }
};

export const useAdminBatches = (criteria: IBatchesCriteria = defaultAdminBatchesCriteria) => {
  return useQuery({
    queryKey: ['admin-batches', hashKey([criteria])],
    queryFn: () => fetchAdminBatches(criteria),
  });
};

export const prefetchAdminBatches = (criteria: IBatchesCriteria = defaultAdminBatchesCriteria) =>
  queryClient.prefetchQuery({
    queryKey: ['admin-batches', hashKey([criteria])],
    queryFn: () => fetchAdminBatches(criteria),
  });
